/** Invisible diagram grid for node snap and orthogonal edge routing. */
export const DIAGRAM_GRID_SIZE = 16;
/** When dragging, snap to peer edges/centers within this distance (px). */
export const DIAGRAM_ALIGN_THRESHOLD = 10;

export function snapCoord(value: number, grid: number = DIAGRAM_GRID_SIZE): number {
  if (!Number.isFinite(value) || grid <= 0) return value;
  return Math.round(value / grid) * grid;
}

/** Round up to the next grid step (for node sizes so ports land on-grid). */
export function ceilToGrid(value: number, grid: number = DIAGRAM_GRID_SIZE): number {
  if (!Number.isFinite(value) || grid <= 0) return value;
  return Math.ceil(value / grid) * grid;
}

export function snapPosition(
  pos: { x: number; y: number },
  grid: number = DIAGRAM_GRID_SIZE,
): { x: number; y: number } {
  return { x: snapCoord(pos.x, grid), y: snapCoord(pos.y, grid) };
}

export type AlignGuide = {
  orientation: 'v' | 'h';
  /** Flow-space coordinate (x for vertical guide, y for horizontal). */
  position: number;
};

export type PeerBox = {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
};

/**
 * Snap to grid, then soft-align to nearby peer edges / centers
 * (horizontal + vertical) when within threshold.
 */
export function snapWithPeerAlign(
  nodeId: string,
  pos: { x: number; y: number },
  size: { width: number; height: number },
  peers: readonly PeerBox[],
  opts?: { grid?: number; threshold?: number },
): { position: { x: number; y: number }; guides: AlignGuide[] } {
  const grid = opts?.grid ?? DIAGRAM_GRID_SIZE;
  const threshold = opts?.threshold ?? DIAGRAM_ALIGN_THRESHOLD;
  let x = snapCoord(pos.x, grid);
  let y = snapCoord(pos.y, grid);
  const guides: AlignGuide[] = [];

  const w = Math.max(1, size.width);
  const h = Math.max(1, size.height);
  const myLeft = x;
  const myRight = x + w;
  const myCx = x + w / 2;
  const myTop = y;
  const myBottom = y + h;
  const myCy = y + h / 2;

  let bestDx = threshold + 1;
  let bestDy = threshold + 1;
  let alignX: number | null = null;
  let alignY: number | null = null;
  let guideV: number | null = null;
  let guideH: number | null = null;

  for (const p of peers) {
    if (p.id === nodeId) continue;
    const left = p.x;
    const right = p.x + p.width;
    const cx = p.x + p.width / 2;
    const top = p.y;
    const bottom = p.y + p.height;
    const cy = p.y + p.height / 2;

    const xCandidates: Array<{ dx: number; nextX: number; guide: number }> = [
      { dx: Math.abs(myLeft - left), nextX: left, guide: left },
      { dx: Math.abs(myRight - right), nextX: right - w, guide: right },
      { dx: Math.abs(myCx - cx), nextX: cx - w / 2, guide: cx },
      { dx: Math.abs(myLeft - right), nextX: right, guide: right },
      { dx: Math.abs(myRight - left), nextX: left - w, guide: left },
    ];
    for (const c of xCandidates) {
      if (c.dx < bestDx && c.dx <= threshold) {
        bestDx = c.dx;
        alignX = c.nextX;
        guideV = c.guide;
      }
    }

    const yCandidates: Array<{ dy: number; nextY: number; guide: number }> = [
      { dy: Math.abs(myTop - top), nextY: top, guide: top },
      { dy: Math.abs(myBottom - bottom), nextY: bottom - h, guide: bottom },
      { dy: Math.abs(myCy - cy), nextY: cy - h / 2, guide: cy },
      { dy: Math.abs(myTop - bottom), nextY: bottom, guide: bottom },
      { dy: Math.abs(myBottom - top), nextY: top - h, guide: top },
    ];
    for (const c of yCandidates) {
      if (c.dy < bestDy && c.dy <= threshold) {
        bestDy = c.dy;
        alignY = c.nextY;
        guideH = c.guide;
      }
    }
  }

  if (alignX != null) {
    x = snapCoord(alignX, grid);
    if (guideV != null) guides.push({ orientation: 'v', position: guideV });
  }
  if (alignY != null) {
    y = snapCoord(alignY, grid);
    if (guideH != null) guides.push({ orientation: 'h', position: guideH });
  }

  return { position: { x, y }, guides };
}
